import { useEffect, useMemo } from "react";

import { splitWordsOnUpperCase } from "@repo/helpers";
import { Loading, RadioGroup, TextBox } from "@repo/ui";

import { useMapSettings } from "../hooks";
import { EnrollmentFilter } from "./enrollmentFilter";
import { MapType, useEmptyFilters, useMapFilters } from "./filterContext";
import { GeographicViewFilters } from "./geographicViewFilters";
import { LiveViewMapFilters } from "./liveViewFilters";
import { RepresentativesFilters } from "./representativesFilter";

export function MapFilters() {
	const { filters, setFilters } = useMapFilters();
	const emptyMapFilter = useEmptyFilters();
	const mapSettingsQuery = useMapSettings();

	function renderFilters() {
		switch (filters.type) {
			case "LiveView":
				return <LiveViewMapFilters />;
			case "GeographicView":
				return <GeographicViewFilters />;
			case "Representatives":
				return <RepresentativesFilters filterType="Rep" />;
			case "Machines":
				return <RepresentativesFilters filterType="Machine" />;
			case "Enrollment":
				return <EnrollmentFilter />;
			default:
				return null;
		}
	}

	function handleLimitPositionsChange(val: string) {
		const parsedVal = parseInt(val);
		if (isNaN(parsedVal)) return;

		if (parsedVal < 1) setFilters({ ...filters, lastPositions: "1" });
		else if (parsedVal > 3000)
			setFilters({ ...filters, lastPositions: mapSettingsQuery.data?.maxPositions.toString() });
		else setFilters({ ...filters, lastPositions: val });
	}

	const radioValues = useMemo(
		() => mapSettingsQuery.data?.mapTypes.map((type) => ({ id: type, value: splitWordsOnUpperCase(type) })) ?? [],
		[mapSettingsQuery.data?.mapTypes],
	);

	useEffect(() => {
		if (mapSettingsQuery.data?.mapTypes) {
			const leads = mapSettingsQuery.data?.showLeads;
			const positions = mapSettingsQuery.data?.showPositions;
			const showSales = mapSettingsQuery.data?.showSales;
			const retailLocations = mapSettingsQuery.data?.showRetailLocations;
			const type = mapSettingsQuery.data?.mapTypes[0] as MapType;
			const lastPositions = mapSettingsQuery.data?.defaultMaxPositions.toString();

			setFilters({ ...filters, leads, positions, showSales, retailLocations, type, lastPositions });
		}
	}, [mapSettingsQuery.data?.mapTypes]);

	return (
		<Loading loading={mapSettingsQuery.isLoading}>
			<Loading loading={filters.type == null}>
				<RadioGroup
					label="Views"
					value={filters.type}
					changeHandler={(val) => setFilters({ ...emptyMapFilter, type: val as MapType })}
					values={radioValues}
					hidden={mapSettingsQuery.data?.mapTypes?.length < 2}
				/>
			</Loading>
			{renderFilters()}
			<TextBox
				label="Limit to Last Number of Positions"
				type="number"
				inputMode="numeric"
				maxlength={4}
				value={filters.lastPositions}
				changeHandler={handleLimitPositionsChange}
			/>
			{filters.type === "LiveView" || filters.type === "GeographicView" ? (
				<span className="tw-font-bold">
					Markers are colored by representative, if one representative, markers are colored by date.
				</span>
			) : null}
		</Loading>
	);
}
